import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ORDER_HISTORY_SERVICE_URL, MANCOS_SERVICE_URL, INDI_CUSTOMERS_URL } from 'src/app/injection-tokens';
import { map, switchMap } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { IndiCustomer } from '../../salesorders/indi-customer';
import { User } from '../../salesorders/user';
import { OrderDetails } from './order-details';
import { MancoOrderRequest, MancoOrderResponse } from './manco-order-request';

export interface MancoCombinedData {
  orderDetails: OrderDetails;
  indiCustomer: IndiCustomer;
  users: User[];
}

@Injectable({
  providedIn: 'root',
})
export class NewMancoService {
  constructor(
    @Inject(ORDER_HISTORY_SERVICE_URL) readonly orderHistoryServiceURL: string,
    @Inject(MANCOS_SERVICE_URL) readonly mancoServiceURL: string,
    @Inject(INDI_CUSTOMERS_URL) readonly indiCustomersUrl: string,
    readonly http: HttpClient
  ) {}

  getCombinedData(orderNumber: string): Observable<MancoCombinedData> {
    return this.getOrderDetails(orderNumber).pipe(
      switchMap(orderDetails =>
        forkJoin({
          indiCustomer: this.getIndiCustomer(orderDetails.customerDetails.customerNo),
          users: this.getUsers(orderDetails.customerDetails.customerNo),
        }).pipe(map(({ indiCustomer, users }) => ({ orderDetails, indiCustomer, users })))
      )
    );
  }

  getOrderDetails(orderNumber: string): Observable<OrderDetails> {
    return this.http
      .get<OrderDetails>(this.orderHistoryServiceURL + 'internal/orderhistory/orderdetails?orderNumber=' + orderNumber)
      .pipe(
        map((orderDetails: OrderDetails) => {
          orderDetails.deliveryAddress.country = this.transformCountry(orderDetails.deliveryAddress.country);
          return orderDetails;
        })
      );
  }

  getIndiCustomer(customerNumber: string): Observable<IndiCustomer> {
    return this.http
      .get<IndiCustomer[]>(this.indiCustomersUrl + '?customerNumber=' + customerNumber + '&fetchSegments=false')
      .pipe(map(list => list[0]));
  }

  getUsers(customerNumber: string): Observable<User[]> {
    return this.http.get<User[]>(this.indiCustomersUrl + '/' + customerNumber + '/user-email-preferences');
  }

  transformCountry(country: string | undefined): string {
    const countryMap: Record<string, string> = {
      NLD: 'NL',
      DEU: 'DE',
    };
    return country ? countryMap[country] || country : '';
  }

  createMancoOrder(request: MancoOrderRequest): Observable<MancoOrderResponse> {
    return this.http.post<MancoOrderResponse>(this.mancoServiceURL + '/orders', request);
  }
}
